import api from '@/lib/api'
import type { ApiResponse, User } from '@/types'
import { hasFlag } from '@/lib/feature-flags'
import type { Task } from '@/lib/tasks-api'

export const AGENT_QUEUE_BUCKETS = [
  'overdue',
  'due_today',
  'upcoming',
  'unscheduled',
  'needs_next_step'
] as const
export type AgentQueueBucket = (typeof AGENT_QUEUE_BUCKETS)[number]
export interface AgentWorkQueue {
  bucket: AgentQueueBucket
  count_unit: 'tasks' | 'leads'
  totals: Record<AgentQueueBucket, number>
  evaluated_at: string
  timezone: string
  day_ends_at: string
  tasks: Task[]
  leads: { id: string; full_name: string; reason: 'missing_next_step' | 'status_unknown' }[]
  pagination: { current_page: number; last_page: number; per_page: number; total: number }
}

export function canUseAgentWorkQueue(user: User | null | undefined): boolean {
  return (
    !!user &&
    hasFlag(user.feature_flags, 'tasks_enabled') &&
    hasFlag(user.feature_flags, 'lead_follow_through_enabled') &&
    hasFlag(user.feature_flags, 'agent_work_queue_enabled') &&
    user.permissions?.some((permission) =>
      ['tasks.view.assigned', 'tasks.view.all'].includes(permission)
    ) &&
    user.permissions?.some((permission) =>
      ['leads.view.assigned', 'leads.view.team', 'leads.view.all'].includes(permission)
    )
  )
}

export async function getAgentWorkQueue(
  bucket: AgentQueueBucket,
  page: number,
  signal?: AbortSignal
) {
  const response = await api.get<ApiResponse<AgentWorkQueue>>('/agent-work-queue', {
    params: { bucket, page, per_page: 20 },
    signal
  })
  return response.data.data
}
